import React, { useEffect, useRef, useState, useCallback } from 'react';
import useLivePrices from '../hooks/useLivePrices';

const TAB_CONFIG = {
  '1H': { interval: '1m',  limit: 60  },
  '4H': { interval: '5m',  limit: 48  },
  '1D': { interval: '15m', limit: 96  },
  '1W': { interval: '1h',  limit: 168 },
  '1M': { interval: '4h',  limit: 180 },
};

const TradingWidget = () => {
  const canvasRef = useRef(null);
  const [activeTab, setActiveTab] = useState('1M');
  const [klineData, setKlineData] = useState(null); 
  const [loading, setLoading] = useState(true);
  const animRef = useRef(null);

  const livePrices = useLivePrices();
  const btc = livePrices['BTC/USD'];
  const rawPrice = btc?.price || 0;
  const price = rawPrice ? rawPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';
  const changeVal = btc?.change ?? null;
  const changeStr = changeVal != null ? (changeVal >= 0 ? '+' : '') + changeVal.toFixed(2) : '—';
  const changeUp = (changeVal ?? 0) >= 0;

  
  const livePriceRef = useRef(rawPrice);
  useEffect(() => { livePriceRef.current = rawPrice; }, [rawPrice]);

  
  const fetchKlines = useCallback(async (tab) => {
    setLoading(true);
    try {
      const { interval, limit } = TAB_CONFIG[tab];
      const res = await fetch(
        `https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=${interval}&limit=${limit}`
      );
      const raw = await res.json();
      
      const closes = raw.map(k => parseFloat(k[4]));
      const highs = raw.map(k => parseFloat(k[2]));
      const lows = raw.map(k => parseFloat(k[3]));
      const vols = raw.map(k => parseFloat(k[5]));
      const periodHigh = Math.max(...highs);
      const periodLow = Math.min(...lows);
      const totalVol = vols.reduce((a, b) => a + b, 0);
      setKlineData({ closes, high: periodHigh, low: periodLow, volume: totalVol });
    } catch {
      setKlineData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchKlines(activeTab); }, [activeTab, fetchKlines]);

  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !klineData || klineData.closes.length < 2) return;

    
    const basecloses = [...klineData.closes];

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    let frame = 0;
    const maxFrame = 60;
    let time = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return rect;
    };
    let { width: w, height: h } = resize();

    
    const minP = Math.min(...basecloses) * 0.9995;
    const maxP = Math.max(...basecloses) * 1.0005;
    const range = maxP - minP || 1;
    const padT = h * 0.08, padB = h * 0.08;
    const chartH = h - padT - padB;

    const toY = (p) => padT + (1 - (p - minP) / range) * chartH;
    const xStep = w / (basecloses.length - 1);

    const lineColor = basecloses[basecloses.length - 1] >= basecloses[0] ? '#38FE60' : '#ff5252';
    const glowColor = basecloses[basecloses.length - 1] >= basecloses[0]
      ? 'rgba(56,254,96,' : 'rgba(255,82,82,';

    if (animRef.current) cancelAnimationFrame(animRef.current);

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      ctx.clearRect(0, 0, w, h);
      time += 0.015;

      const progress = Math.min(frame / maxFrame, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const visible = Math.max(2, Math.floor(basecloses.length * ease));

      
      const pts = basecloses.slice(0, visible);
      if (livePriceRef.current && visible === basecloses.length) {
        pts[pts.length - 1] = livePriceRef.current;
      }

      
      ctx.setLineDash([3, 5]);
      ctx.strokeStyle = 'rgba(255,255,255,0.03)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= 4; i++) {
        const y = padT + (chartH / 4) * i;
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      }
      ctx.setLineDash([]);

      
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.font = '9px monospace';
      ctx.textAlign = 'right';
      for (let i = 0; i <= 4; i++) {
        const val = maxP - (range / 4) * i;
        const y = padT + (chartH / 4) * i;
        ctx.fillText('$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 }), w - 4, y + 3);
      }

      

      
      ctx.beginPath();
      ctx.moveTo(0, h);
      pts.forEach((p, i) => {
        const x = i * xStep;
        const y = toY(p);
        if (i === 0) ctx.lineTo(x, y);
        else {
          const px = (i - 0.5) * xStep;
          ctx.quadraticCurveTo(px, toY(pts[i - 1]), x, y);
        }
      });
      ctx.lineTo((pts.length - 1) * xStep, h);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, padT, 0, h);
      grad.addColorStop(0, glowColor + '0.12)');
      grad.addColorStop(0.6, glowColor + '0.04)');
      grad.addColorStop(1, glowColor + '0)');
      ctx.fillStyle = grad;
      ctx.fill();

      
      ctx.beginPath();
      pts.forEach((p, i) => {
        const x = i * xStep;
        const y = toY(p);
        if (i === 0) ctx.moveTo(x, y);
        else {
          const px = (i - 0.5) * xStep;
          ctx.quadraticCurveTo(px, toY(pts[i - 1]), x, y);
        }
      });
      ctx.strokeStyle = glowColor + '0.15)';
      ctx.lineWidth = 8;
      ctx.stroke();

      
      ctx.beginPath();
      pts.forEach((p, i) => {
        const x = i * xStep;
        const y = toY(p);
        if (i === 0) ctx.moveTo(x, y);
        else {
          const px = (i - 0.5) * xStep;
          ctx.quadraticCurveTo(px, toY(pts[i - 1]), x, y);
        }
      });
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1.8;
      ctx.stroke();

      
      const ex = (pts.length - 1) * xStep;
      const ey = toY(pts[pts.length - 1]);
      const pulse = 0.4 + Math.sin(time * 6) * 0.3;
      ctx.strokeStyle = glowColor + (pulse * 0.4) + ')';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(ex, ey, 7 + pulse * 3, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = lineColor;
      ctx.shadowColor = lineColor;
      ctx.shadowBlur = 10;
      ctx.beginPath(); ctx.arc(ex, ey, 3, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;

      if (frame < maxFrame) frame++;
      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [klineData]); 

  const tabs = ['1H', '4H', '1D', '1W', '1M'];
  const high = klineData?.high?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '—';
  const low = klineData?.low?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '—';
  const vol = klineData?.volume
    ? klineData.volume > 1e9 ? (klineData.volume / 1e9).toFixed(1) + 'B'
      : klineData.volume > 1e6 ? (klineData.volume / 1e6).toFixed(0) + 'M' : klineData.volume.toFixed(0)
    : '—';

  return (
    <div className="w-full h-full flex flex-col bg-[#0d0d0f] rounded-2xl overflow-hidden border border-[#ffffff06]">
            <div className="px-5 pt-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#f7931a]/10 border border-[#f7931a]/20 flex items-center justify-center">
              <svg viewBox="0 0 32 32" className="w-5 h-5">
                <circle cx="16" cy="16" r="16" fill="#f7931a" />
                <path d="M22.5 14.2c.3-2-1.2-3-3.3-3.8l.7-2.7-1.6-.4-.6 2.6c-.4-.1-.9-.2-1.3-.3l.7-2.6-1.7-.4-.7 2.7c-.4-.1-.7-.2-1-.2l-2.3-.6-.4 1.7s1.2.3 1.2.3c.7.2.8.6.8 1l-.8 3.2c0 0 .1 0 .2.1h-.2l-1.1 4.5c-.1.2-.3.5-.7.4 0 0-1.2-.3-1.2-.3l-.8 1.9 2.2.5c.4.1.8.2 1.2.3l-.7 2.7 1.6.4.7-2.7c.4.1.9.2 1.3.3l-.7 2.7 1.7.4.7-2.7c2.8.5 4.9.3 5.8-2.2.7-2-.1-3.2-1.5-3.9 1.1-.3 1.9-1 2.1-2.5zm-3.7 5.2c-.5 2-4 .9-5.1.7l.9-3.7c1.1.3 4.7.8 4.2 3zm.5-5.3c-.5 1.8-3.3.9-4.3.7l.8-3.3c1 .2 4 .7 3.5 2.6z" fill="white"/>
              </svg>
            </div>
            <div>
              <div className="text-white text-sm font-semibold">BTC / USD</div>
              <div className="text-[#555] text-[10px] font-mono">Bitcoin</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-[#38FE60]/15 bg-[#38FE60]/05">
            <div className="w-1.5 h-1.5 rounded-full bg-[#38FE60] animate-pulse" />
            <span className="text-[9px] text-[#38FE60] font-mono">Live</span>
          </div>
        </div>

                <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-white text-2xl font-bold tracking-tight">${price}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-sm font-medium ${changeUp ? 'text-[#38FE60]' : 'text-[#ff5252]'}`}>{changeStr}%</span>
              <span className="text-[#444] text-xs">24h</span>
            </div>
          </div>
          <div className="flex gap-1">
            {tabs.map(t => (
              <button key={t} onClick={() => setActiveTab(t)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-medium transition-all ${
                  activeTab === t
                    ? 'bg-[#38FE60]/10 text-[#38FE60] border border-[#38FE60]/20'
                    : 'text-[#555] hover:text-[#888] border border-transparent'
                }`}>{t}</button>
            ))}
          </div>
        </div>
      </div>

            <div className="flex-1 px-1 min-h-0 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#38FE60] animate-pulse" />
              <span className="text-[10px] text-[#555] font-mono">Loading chart…</span>
            </div>
          </div>
        )}
        <canvas ref={canvasRef} className="w-full h-full" style={{ width: '100%', height: '100%' }} />
      </div>

            <div className="px-5 py-3 grid grid-cols-3 gap-3 border-t border-[#ffffff06]">
        {[
          { label: 'High', value: `$${high}`, color: 'text-[#38FE60]' },
          { label: 'Low',  value: `$${low}`,  color: 'text-[#ff5252]' },
          { label: 'Volume', value: vol,       color: 'text-white' },
        ].map(s => (
          <div key={s.label} className="text-center">
            <div className="text-[8px] text-[#444] font-mono uppercase tracking-wider mb-0.5">{s.label}</div>
            <div className={`text-xs font-semibold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TradingWidget;
